import React, { FC, useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import { listApiGroups } from 'k8s/components/access-control/api-groups/new-actions'
import Checkbox from 'core/elements/input/Checkbox'
import withFormContext from 'core/components/validatedForm/withFormContext'
import { compose, projectAs } from 'app/utils/fp'
import { assocPath, dissocPath, hasPath, ifElse, identity, sortBy, prop } from 'ramda'
import { emptyObj } from 'utils/fp'
import Progress from 'core/components/progress/Progress'
import { setRbacObject } from './helper'
import Theme from 'core/themes/model'
import { IRbacAPIGroupResourceVerb, IRbacAPIGroup, IRbacAPIGroupResource } from './api-groups/model'
import PicklistDefault from 'core/elements/dropdown/AsyncDropdown'
import { Chip } from '@material-ui/core'
import Text from 'core/elements/Text'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import generateTestId from 'utils/test-helpers'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { apiGroupsSelector } from 'k8s/components/access-control/api-groups/selectors'
import Dropdown from 'core/elements/dropdown'

const Picklist: any = PicklistDefault // types on forward ref .js file dont work well.

interface IApiGroupWithRoles {
  [apiGroup: string]: IApiGroupResource
}
interface IApiGroupResource {
  [resourceName: string]: IApiGroupResourceVerbs
}
type IApiGroupResourceVerbs = { [key in IRbacAPIGroupResourceVerb]: boolean }
interface IRbacChecklistState {
  group: string
  resource: string
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    margin: theme.spacing(0, 1),
  },
  header: {
    display: 'flex',
    paddingBottom: '2px',
    marginBottom: '5px',
  },
  group: {
    display: 'grid',
    gridGap: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  resource: {
    display: 'grid',
    gridGap: theme.spacing(),
  },
  resourceGroup: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 8px',
  },
  resourceName: {
    fontWeight: 600,
  },
  verbs: {
    display: 'grid',
    margin: '16px 0',
    gridTemplateColumns: 'repeat(3, 140px)',
  },
  groupHeader: {
    fontSize: theme.spacing(2),
  },
  verbContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr) 8px',
    alignItems: 'center',
    justifyItems: 'center',
  },
  spacer: {
    margin: theme.spacing(2, 0),
  },
  icon: {
    justifySelf: 'center',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  actionColumn: {
    display: 'grid',
  },
  selectedGroup: {
    marginBottom: theme.spacing(),
    display: 'grid',
    gridTemplateColumns: '1fr 40px',
    background: hexToRgbaCss(theme.palette.primary.main, 0.1),
    padding: theme.spacing(1, 2),
    borderRadius: '4px',
    border: '1px solid rgba(74, 163, 223, 0.5)',
  },
}))

const RbacChecklist = ({ clusterId, onChange, value, initialRules, ...rest }) => {
  const classes = useStyles({})
  const [editingGroup, setEditingGroup] = useState<IRbacChecklistState>(null)
  const [checkedItems, setCheckedItems] = useState<IApiGroupWithRoles>(
    emptyObj as IApiGroupWithRoles,
  )

  const { loading: loadingApiGroups, reload: reloadApiGroups } = useListAction(listApiGroups, {
    params: { clusterId },
    requiredParams: ['clusterId'],
  })
  const apiGroups = useSelectorWithParams(apiGroupsSelector, { clusterId })
  const sortedApiGroups = useMemo(() => {
    const sortByName = sortBy(prop('name'))
    return sortByName(apiGroups) as IRbacAPIGroup[]
  }, [apiGroups])

  useEffect(() => {
    // Todo: When switching back to a cluster where API groups have already
    // been loaded, do not reload. Tricky to do with this reload
    reloadApiGroups(true)
  }, [clusterId])

  useEffect(() => {
    if (!initialRules || !apiGroups || !apiGroups.length) {
      return
    }
    const rules = setRbacObject(initialRules, apiGroups)
    setCheckedItems(rules)
    onChange(rules)
  }, [initialRules, apiGroups])

  const handleCheck = useCallback(
    (group, resource, verb) => {
      const path = [group, resource, verb]
      const newCheckedItems = ifElse(
        hasPath(path),
        dissocPath(path),
        assocPath(path, true),
      )(checkedItems)
      setCheckedItems(newCheckedItems)
      onChange(newCheckedItems)
    },
    [checkedItems],
  )

  const handleDelete = useCallback(
    (group, resource) => {
      const path = [group, resource]
      const newCheckedItems = ifElse(hasPath(path), dissocPath(path), identity)(checkedItems)
      setCheckedItems(newCheckedItems)
      onChange(newCheckedItems)
    },
    [checkedItems],
  )

  return (
    <Progress loading={loadingApiGroups} renderContentOnMount>
      <div className={classes.container} data-testid="rbac-checklist-container">
        <AddRbacApiGroup
          groups={sortedApiGroups}
          selectedItems={checkedItems}
          onChange={handleCheck}
          editGroup={editingGroup}
          data-testid="add-rbac-api-group"
        />
        <br className={classes.spacer} data-testid="rbac-checklist-spacer" />
        <SelectedRbacApiGroups
          groups={checkedItems}
          onEdit={setEditingGroup}
          onDelete={handleDelete}
          data-testid="selected-rbac-api-groups"
        />
      </div>
    </Progress>
  )
}

interface IAddRbacApiGroupProps {
  groups: IRbacAPIGroup[]
  selectedItems: IApiGroupWithRoles
  editGroup: IRbacChecklistState
  onChange: (group: string, resource: string, verb: string) => void
}

const AddRbacApiGroup: FC<IAddRbacApiGroupProps> = ({
  groups,
  selectedItems,
  onChange,
  editGroup,
}) => {
  const groupRef = useRef(undefined)
  const resourceRef = useRef(undefined)
  const classes = useStyles({})

  const [selectedGroup, setSelectedGroup] = useState<IRbacAPIGroup>(emptyObj as any)
  const [selectedResource, setSelectedResource] = useState<IRbacAPIGroupResource>(emptyObj as any)

  useEffect(() => {
    if (!editGroup) return
    if (editGroup.group && editGroup.resource) {
      const selectedApiGroup: IRbacAPIGroup =
        groups.find((group) => group.name === editGroup.group) || ({} as any)
      const selectedApiGroupResource = (selectedApiGroup.resources || []).find(
        (resource) => resource.name === editGroup.resource,
      )
      setSelectedGroup(selectedApiGroup || ({} as any))
      setSelectedResource(selectedApiGroupResource || ({} as any))
    }
  }, [editGroup])

  useEffect(() => {
    if (groups.length === 0 || !selectedGroup.name || !selectedGroup.resources?.length) {
      const selectedApiGroup = groups.find((group) => group.name === selectedGroup?.name) || null
      const selectedGroupResource = (selectedApiGroup?.resources || [])[0] || null

      setSelectedGroup(selectedApiGroup || (emptyObj as any))
      setSelectedResource(selectedGroupResource || (emptyObj as any))
    }
  }, [groups, selectedGroup])

  const handleGroupChange = (value) => {
    const selectedApiGroup = groups.find((group) => group.name === value)
    const selectedGroupResource = (selectedApiGroup.resources || [])[0]
    setSelectedGroup(selectedApiGroup)
    setSelectedResource(selectedGroupResource)
  }
  const handleResourceChange = (value) => {
    const selectedGroupResource = (selectedGroup.resources || []).find(
      (resource) => resource.name === value,
    )
    setSelectedResource(selectedGroupResource || ({} as any))
  }

  // const groupOptions = pluck('name', groups)
  const groupOptions = useMemo(() => projectAs({ label: 'name', value: 'name' }, groups || []), [
    groups,
  ])
  // const resourceOptions = pluck('name', selectedGroup.resources || [])
  const resourceOptions = useMemo(
    () => projectAs({ label: 'name', value: 'name' }, selectedGroup?.resources || []),
    [selectedGroup.resources],
  )
  const verbs = selectedResource && selectedResource.verbs ? selectedResource.verbs : []
  return (
    <>
      <div className={classes.group} data-testid="api-group-dropdown">
        <Dropdown
          // ref={groupRef}
          label="API Group"
          name="selectedGroup"
          value={selectedGroup.name}
          onChange={handleGroupChange}
          items={groupOptions}
          showAll={false}
          data-testid="api-group-select"
        />
        <Dropdown
          // ref={resourceRef}
          label="Resources"
          name="selectedResource"
          value={selectedResource?.name}
          onChange={handleResourceChange}
          items={resourceOptions}
          showAll={false}
          data-testid="resource-select"
          showNone={resourceOptions?.length === 0}
        />
      </div>
      <br />
      <Text variant="subtitle2" data-testid="verb-selection-label">
        Choose which verbs to add to this resource
      </Text>
      <div className={classes.verbs} data-testid="verbs-checkboxes">
        {verbs.map((verb) => (
          <Checkbox
            key={verb}
            label={verb}
            data-testid={generateTestId(verb, 'checkbox')}
            checked={hasPath([selectedGroup.name, selectedResource.name, verb], selectedItems)}
            onChange={() => onChange(selectedGroup.name, selectedResource.name, verb)}
          />
        ))}
        {!Object.keys(verbs).length && (
          <Text variant="body2" className={classes.noVerbsMessage}>
            No verbs available
          </Text>
        )}
      </div>
    </>
  )
}

interface ISelectedRbacApiGroupProps {
  groups: IApiGroupWithRoles
  onEdit: (params: IRbacChecklistState) => void
  onDelete: (group: string, resource: string) => void
}
const SelectedRbacApiGroups: FC<ISelectedRbacApiGroupProps> = ({ groups, onEdit, onDelete }) => {
  const classes = useStyles({})
  return (
    <>
      {Object.entries(groups).map(([group, resources = {}], groupIndex) => (
        <div key={group} data-testid={`group-${groupIndex}-${group}`}>
          {Object.entries(resources).map(([resource, verbs = {}], resourceIndex) => {
            return (
              <div
                key={`${group}-${resource}`}
                className={classes.selectedGroup}
                data-testid={`resource-${resourceIndex}-${resource}`}
              >
                <div className={classes.resource}>
                  <div className={classes.resourceGroup}>
                    <Text variant="body1" className={classes.groupHeader}>
                      {group}
                    </Text>
                    <Text variant="body1">{resource}</Text>
                  </div>
                  <div className={classes.verbContainer}>
                    {Object.entries(verbs).map(([verb, value], verbIndex) =>
                      value ? (
                        <Chip
                          data-testid={`verb-${verbIndex}-${verb}`}
                          key={`${group}-${resource}-${verb}`}
                          label={verb}
                        />
                      ) : null,
                    )}
                  </div>
                </div>
                <div className={classes.actionColumn}>
                  <FontAwesomeIcon
                    className={classes.icon}
                    onClick={() => onDelete(group, resource)}
                    data-testid={`delete-${groupIndex}-${resourceIndex}-${group}-${resource}`}
                  >
                    times
                  </FontAwesomeIcon>
                  <FontAwesomeIcon
                    className={classes.icon}
                    onClick={() => onEdit({ group, resource })}
                    data-testid={`edit-${groupIndex}-${resourceIndex}-${group}-${resource}`}
                  >
                    pen
                  </FontAwesomeIcon>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

export default compose(withFormContext)(RbacChecklist)
